/**
 * Billing account invoices subview — the account's invoice table, a compare
 * mode toggle, and manual recurring-invoice regeneration. Routed at
 * `/billing/accounts/:accountId/invoices`.
 *
 * Contains:
 * - AccountInvoicesView — routed child page (default export)
 */
import SyncIcon from '@mui/icons-material/Sync';
import { Box, Button, Stack } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { generateAccountInvoices } from '@sophocles/api/billing';
import { addToast } from '@sophocles/features/toast';
import { useBillingAccount } from '@sophocles/views/billing/BillingAccountLayout';
import { InvoiceTable } from '@sophocles/views/billing/components';
import InvoiceCompare from '@sophocles/views/billing/components/InvoiceCompare';

function AccountInvoicesView() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { account } = useBillingAccount();
  const [isCompareMode, setIsCompareMode] = useState(false);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 2, pt: 2, mb: 1, flexShrink: 0 }}
      >
        <Button onClick={() => setIsCompareMode(prev => !prev)}>
          {isCompareMode ? t('billing.account.stopComparing') : t('billing.account.compareInvoices')}
        </Button>
        <Button
          startIcon={<SyncIcon />}
          onClick={() => {
            generateAccountInvoices(account?.id).then(() => {
              dispatch(addToast({ message: t('billing.account.invoicesGenerating'), level: 'success' }));
            });
          }}
        >
          {t('billing.account.regenerateRecurring')}
        </Button>
      </Stack>
      {isCompareMode ? (
        <Box sx={{ px: 2, pb: 2, overflow: 'auto' }}>
          <InvoiceCompare accountId={account?.id} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, px: 2, pb: 2 }}>
          <InvoiceTable accountId={account?.id} />
        </Box>
      )}
    </>
  );
}

export default AccountInvoicesView;
